import { createServerSideRequestOption, getBaseUrl } from "@/redux/config";

export class CustomerService {
  private static readonly BASE_PATH = "/customers";

  private static async fetchWithServerRequest<T>(options: {
    url: string;
    request: Request;
    method: serverSideHttpMethod;
    body?: unknown;
  }): Promise<T | null> {
    const res = await createServerSideRequestOption({
      url: `${getBaseUrl()}${options.url}`,
      request: options.request,
      method: options.method,
      body: options.body,
    });

    return res.ok ? res.json() : null;
  }

  static getEndpointUrl(params: BaseCustomerParams, suffix = "") {
    return `/c/${params.companyId}/services/${params.serviceId}${this.BASE_PATH}${suffix}`;
  }

  static async addCustomerAccount(
    params: CustomerSystemRefParams,
    data: CustomerAccountData,
    request: Request
  ): Promise<ServiceCustomerAccount | null> {
    return this.fetchWithServerRequest({
      url: this.getEndpointUrl(params, `/accounts/${params.customerSystemRef}`),
      request,
      method: "POST",
      body: data,
    });
  }

  static async editCustomerAccount(
    params: BaseCustomerParams,
    data: CustomerAccountData,
    request: Request
  ): Promise<ServiceCustomerAccount | null> {
    return this.fetchWithServerRequest({
      url: this.getEndpointUrl(params, "/accounts"),
      request,
      method: "PUT",
      body: data,
    });
  }

  static async listCustomerAccounts(
    params: CustomerSystemRefParams,
    request: Request
  ): Promise<ServiceCustomerAccount[] | null> {
    return this.fetchWithServerRequest({
      url: this.getEndpointUrl(params, `/accounts/${params.customerSystemRef}`),
      request,
      method: "GET",
    });
  }

  // Additional methods following the same pattern for devices and customers...
  static async addCustomerDevice(
    params: CustomerSystemRefParams,
    data: CustomerDeviceData,
    request: Request
  ): Promise<ServiceCustomerDevice | null> {
    return this.fetchWithServerRequest({
      url: this.getEndpointUrl(params, "/devices"),
      request,
      method: "POST",
      body: data,
    });
  }

  static async addCustomer(
    params: BaseCustomerParams,
    data: CustomerData,
    request: Request
  ): Promise<ServiceCustomer | null> {
    return this.fetchWithServerRequest({
      url: this.getEndpointUrl(params),
      request,
      method: "POST",
      body: data,
    });
  }

  static async editCustomer(
    params: BaseCustomerParams & { customerId: number },
    data: CustomerData,
    request: Request
  ): Promise<ServiceCustomer | null> {
    return this.fetchWithServerRequest({
      url: this.getEndpointUrl(params, `/${params.customerId}`),
      request,
      method: "PUT",
      body: data,
    });
  }

  static async listCustomers(
    params: BaseCustomerParams,
    filter: CustomerFilter,
    request: Request
  ): Promise<ServiceCustomer[] | null> {
    return this.fetchWithServerRequest({
      url: this.getEndpointUrl(params, "/all"),
      request,
      method: "POST",
      body: filter,
    });
  }
}
